import React, { useState, useEffect } from 'react';
import {
  FileText,
  User,
  Clock,
  Printer,
  Share2,
  CheckCircle2,
  Activity,
  CreditCard,
  Building2,
  Sparkles,
  ChevronRight,
  RotateCcw,
} from 'lucide-react';
import { Drawer } from '../common/Drawer';
import { Button } from '../common/Button';
import { useDatabase } from '../../db/DatabaseProvider';
import { useBaseCurrency, usePaymentMethods, useStaff, useCustomers } from '../../db/hooks/useLookupData';
import { formatLocal } from '../../lib/currency';
import { dayjs } from '../../lib/dayjs';
import type { TransactionDocType } from '../../db/schemas';

interface InvoiceDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId?: string | null;
  onRefundRequest?: (transactionId: string) => void;
}

export function InvoiceDetailsDrawer({
  isOpen,
  onClose,
  transactionId,
  onRefundRequest,
}: InvoiceDetailsDrawerProps) {
  const db = useDatabase();
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  const { paymentMethods = [] } = usePaymentMethods();
  const { staff = [] } = useStaff();
  const { customers = [] } = useCustomers();

  const [activeTab, setActiveTab] = useState<'details' | 'activity'>('details');
  const [transaction, setTransaction] = useState<TransactionDocType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!db || !transactionId || !isOpen) {
      setTransaction(null);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    db.transactions
      .findOne({ selector: { id: transactionId } })
      .exec()
      .then((doc) => {
        if (isMounted) {
          setTransaction(doc ? doc.toJSON() : null);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.error('[InvoiceDetailsDrawer] Error fetching transaction:', err);
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [db, transactionId, isOpen]);

  if (!isOpen) return null;

  const rawDate = transaction?.date || transaction?.created_at;
  const customerName = transaction?.customer_name || (customers.find((c) => c.id === transaction?.customer_id)?.name) || 'Venta General en Mostrador';
  const customerDoc = customers.find((c) => c.id === transaction?.customer_id);
  const shortId = transaction?.id ? transaction.id.slice(0, 8) : (transactionId ? transactionId.slice(0, 8) : '');

  const dateFormatted = rawDate
    ? dayjs(rawDate).format('ddd D MMM, YYYY')
    : 'Fecha desconocida';
  const timeFormatted = rawDate
    ? dayjs(rawDate).format('HH:mm A')
    : '';

  const totalDivisa = transaction?.total_amount ?? 0;
  const tipAmount = (transaction?.metadata as any)?.tip || 0;
  const tipOperatorId = (transaction?.metadata as any)?.tip_operator_id;
  const tipOperator = staff.find(p => p.id === tipOperatorId);
  const rateValue = transaction?.payments?.[0]?.exchange_rate || 1;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Detalles de Venta #${shortId}`}
      subtitle={`${dateFormatted} ${timeFormatted ? '• ' + timeFormatted : ''}`}
      icon={FileText}
      maxWidth="3xl"
      noPadding
    >
      <div className="h-full flex flex-col justify-between">
        
        {/* Navigation Tabs Header */}
        <div className="flex items-center gap-2 px-6 py-3 bg-surface-secondary/40 border-b border-border/60">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-4 py-2 rounded-xl text-xs font-extrabold transition-all cursor-pointer ${
              activeTab === 'details'
                ? 'bg-brand-orange text-white shadow-md shadow-brand-orange/20'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-secondary'
            }`}
          >
            📋 Detalles de Venta
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-4 py-2 rounded-xl text-xs font-extrabold transition-all cursor-pointer ${
              activeTab === 'activity'
                ? 'bg-brand-orange text-white shadow-md shadow-brand-orange/20'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-secondary'
            }`}
          >
            📊 Registro de Actividad
          </button>
        </div>

        {/* Tab Content Body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
          {isLoading && (
            <div className="p-12 text-center text-xs font-bold text-text-tertiary">
              Cargando comprobante de venta...
            </div>
          )}

          {!isLoading && !transaction && (
            <div className="p-12 text-center text-xs font-bold text-text-tertiary">
              No se encontró el registro de la transacción especificada.
            </div>
          )}

          {!isLoading && transaction && activeTab === 'details' && (
            <div className="space-y-6 animate-fade-in">
              
              {/* Receipt Status Header Banner */}
              <div className="bg-surface border border-border p-5 rounded-2xl shadow-xs flex items-center justify-between">
                <div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 uppercase tracking-wider mb-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {transaction.status || 'Completada'}
                  </span>
                  <h3 className="text-xl font-black text-text-primary">
                    Venta #{shortId}
                  </h3>
                  <p className="text-xs text-text-secondary">
                    Registrado por: {transaction.registered_by || 'Sistema'}
                  </p>
                </div>

                <div className="text-right">
                  <span className="text-xs text-text-tertiary block">Monto Total Recibido</span>
                  <span className="text-2xl font-black text-brand-orange">
                    {currencySymbol}{totalDivisa.toFixed(2)}
                  </span>
                  <span className="text-xs text-text-tertiary font-mono block">
                    {baseCurrency} (Tasa BCV: {rateValue.toFixed(2)} VES)
                  </span>
                </div>
              </div>

              <div className="bg-surface-secondary/40 border border-border p-4 rounded-2xl flex items-center gap-3 my-2">
                <div className="w-10 h-10 rounded-full bg-brand-sky/10 border border-brand-sky/30 flex items-center justify-center text-brand-sky font-extrabold text-sm">
                  {customerName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4 className="font-bold text-sm text-text-primary">
                    {customerName}
                  </h4>
                  <p className="text-xs text-text-secondary">
                    {customerDoc?.email || customerDoc?.phone || 'Cliente Mostrador'}
                  </p>
                </div>
              </div>

              {/* Line Items Breakdown */}
              <div className="space-y-3 pt-2">
                <h4 className="text-xs font-extrabold uppercase tracking-wider text-text-tertiary flex items-center gap-1.5 mb-2">
                  <Sparkles className="w-3.5 h-3.5 text-brand-orange" />
                  Ítems Cobrados
                </h4>

                <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-xs divide-y divide-border/60">
                  {(transaction.items || []).map((item, idx) => {
                    const price = item.unit_price ?? (item as any).unit_price ?? 0;
                    const subt = item.subtotal ?? (item as any).subtotal ?? 0;
                    return (
                      <div key={item.id || idx} className="p-4 flex items-center justify-between">
                        <div>
                          <h5 className="font-bold text-sm text-text-primary">
                            {item.concept}
                          </h5>
                          <span className="text-xs text-text-secondary">
                            Cantidad: {item.quantity} • Precio unitario: {currencySymbol}{price.toFixed(2)}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="font-extrabold text-sm text-text-primary">
                            {currencySymbol}{subt.toFixed(2)}
                          </span>
                          <span className="block text-[10px] text-text-tertiary">{baseCurrency}</span>
                        </div>
                      </div>
                    );
                  })}

                  {tipAmount > 0 && (
                    <div className="p-4 flex items-center justify-between bg-emerald-500/5">
                      <div>
                        <h5 className="font-bold text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5" /> Propina Directa
                        </h5>
                        <span className="text-xs text-text-secondary">
                          Asignada a: {tipOperator?.name || staff.find(p => p.id === (transaction.items?.[0] as any)?.operator_id)?.name || 'Personal de Guardia'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="font-extrabold text-sm text-emerald-600 dark:text-emerald-400">
                          +{currencySymbol}{tipAmount.toFixed(2)}
                        </span>
                        <span className="block text-[10px] text-text-tertiary">{baseCurrency}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payments Breakdown */}
              <div className="space-y-3">
                <h4 className="text-xs font-extrabold uppercase tracking-wider text-text-tertiary flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5 text-brand-sky" />
                  Métodos de Pago Utilizados
                </h4>

                <div className="bg-surface border border-border rounded-2xl p-4 space-y-3 shadow-xs">
                  {(transaction.payments || []).map((pay, idx) => {
                    const matchedMethod = paymentMethods.find((m) => m.id === pay.payment_method_id);
                    const payAmount = pay.base_equivalent ?? pay.received_amount ?? (pay as any).amount ?? 0;
                    const isVESMethod = matchedMethod?.currency === 'VES';
                    const localAmount = isVESMethod && pay.exchange_rate ? pay.received_amount : (payAmount * (pay.exchange_rate || 1));

                    return (
                      <div key={pay.id || idx} className="flex justify-between items-center text-xs">
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-1 rounded-lg bg-surface-tertiary text-text-primary font-bold border border-border">
                            {matchedMethod ? matchedMethod.name : 'Método de Pago'}
                          </span>
                          {isVESMethod && (
                            <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-brand-sky/10 text-brand-sky border border-brand-sky/20">
                              VES / Bs.S
                            </span>
                          )}
                        </div>
                        <div className="text-right font-extrabold text-text-primary">
                          {isVESMethod ? (
                            <>
                              <span className="text-sm block">{formatLocal(localAmount)}</span>
                              <span className="text-xs font-semibold text-text-tertiary block">({currencySymbol}{payAmount.toFixed(2)})</span>
                            </>
                          ) : (
                            <span className="text-sm block">{currencySymbol}{payAmount.toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {!isLoading && transaction && activeTab === 'activity' && (
            <div className="space-y-4 animate-fade-in p-2">
              <h4 className="text-xs font-extrabold uppercase tracking-wider text-text-tertiary flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5 text-brand-orange" />
                Historial de Registro y Modificaciones
              </h4>

              <div className="space-y-3 border-l-2 border-brand-orange/30 pl-4">
                <div className="relative space-y-1">
                  <span className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-brand-orange" />
                  <p className="text-xs font-bold text-text-primary">
                    Venta registrada y cobrada exitosamente
                  </p>
                  <p className="text-[11px] text-text-tertiary">
                    {dateFormatted} {timeFormatted} • Usuario: {transaction.registered_by || 'Sistema'}
                  </p>
                </div>

                <div className="relative space-y-1 pt-2">
                  <span className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-brand-sky" />
                  <p className="text-xs font-bold text-text-primary">
                    Tasa de cambio oficial BCV aplicada
                  </p>
                  <p className="text-[11px] text-text-tertiary">
                    Valor de conversión: {rateValue} VES por {baseCurrency}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Footer */}
        {transaction && (
          <div className="p-4 px-6 border-t border-border bg-surface-secondary/80 backdrop-blur-md flex flex-wrap items-center gap-2 w-full">
            <Button
              variant="secondary"
              size="sm"
              icon={Printer}
              onClick={() => window.print()}
            >
              Imprimir
            </Button>

            <Button
              variant="secondary"
              size="sm"
              icon={Share2}
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: `Comprobante Depilaway #${shortId}`,
                    text: `Recibo de Venta #${shortId} por ${currencySymbol}${totalDivisa} ${baseCurrency}`,
                  });
                }
              }}
            >
              Compartir
            </Button>

            {onRefundRequest && (
              <Button
                variant="danger"
                size="sm"
                icon={RotateCcw}
                onClick={() => onRefundRequest(transaction.id)}
                className="ml-auto"
              >
                Devolución
              </Button>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
}
